/**
 * Reparto de un gasto entre productos (spec 16, sección 4), igual en Compras y en Gastos: la dueña escribe el monto de
 * cada producto (las casillas abren vacías; el sistema no propone ni prorratea) y ve en vivo el total, lo asignado, lo
 * que falta y, por producto, el gasto por unidad y el costo real resultante. No se guarda hasta que cuadre al centavo.
 * - `MatrizReparto`: compra con artículos. Filas = sus artículos; una columna por gasto (servicio cargado al costo o
 *   gasto sin factura de la compra), cada una con su cuadre.
 * - `RepartoLibre`: compra solo de servicios y Gastos. Productos sueltos (buscador) o los de una compra anterior, con
 *   las unidades entre las que se divide (existencia actual o las de la compra, editables, nunca cero).
 */
import { Money, type CompraParaRepartirDTO, type ProductoCostoDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Input } from '@/componentes/ui/input.tsx';
import { get } from '@/lib/api.ts';
import { parseMonto } from '@/lib/montos.ts';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';

const usd = (m: Money) => `$ ${m.toDisplay()}`;
/** Costo por unidad con cuatro decimales (0,0333 $ no puede verse como 0,03): «11,5833». */
export function con4(m: Money): string {
  const [ent = '0', dec = '0000'] = m.toDB().replace('-', '').split('.');
  return `${m.esNegativo() ? '-' : ''}${ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec.slice(0, 4)}`;
}
/** Monto escrito → Money (vacío o inválido = null). */
export const leer = (texto: string): Money | null => (texto.trim() === '' ? null : parseMonto(texto));
const dosDecimales = (m: Money) => m.redondear2().eq(m);

/** Estado del cuadre de una columna o de un reparto: asignado, falta y si cuadra al centavo. */
export function cuadre(total: string, montos: string[]): { total: Money; asignado: Money; falta: Money; cuadra: boolean } {
  const t = leer(total) ?? Money.cero();
  const valores = montos.map(leer);
  const asignado = Money.sumar(valores.filter((v): v is Money => v !== null));
  const validos = valores.every((v) => v === null || (v.esPositivo() && dosDecimales(v)));
  return { total: t, asignado, falta: t.sub(asignado), cuadra: t.esPositivo() && validos && asignado.eq(t) && valores.some((v) => v !== null) };
}

function PieCuadre({ c, testid }: { c: ReturnType<typeof cuadre>; testid: string }) {
  return (
    <div className="space-y-0.5 text-[11px]" data-testid={testid}>
      <p className="flex justify-between gap-2"><span>Gasto</span><b className="num">{usd(c.total)}</b></p>
      <p className="flex justify-between gap-2"><span>Asignado</span><b className="num">{usd(c.asignado)}</b></p>
      <p className={`flex justify-between gap-2 ${c.cuadra ? 'text-mg-verde-oscuro' : 'text-destructive'}`}><span>{c.falta.esNegativo() ? 'Sobra' : 'Falta'}</span><b className="num" data-testid={`${testid}-falta`}>{usd(c.falta.abs())}</b></p>
      {c.cuadra ? <Badge variant="ok">Cuadra</Badge> : <Badge variant="critico">No cuadra</Badge>}
    </div>
  );
}

export interface ColumnaReparto {
  clave: string;
  titulo: string;
  total: string;
  conFactura: boolean;
  montos: Record<string, string>;
}
export interface ArticuloReparto { producto_id: string; codigo: string; nombre: string; cantidad: string; costo_factura: string }

/** Compra con artículos: una columna por gasto, cada una con su propio cuadre; costo real resultante por artículo. */
export function MatrizReparto({ articulos, columnas, onMonto }: { articulos: ArticuloReparto[]; columnas: ColumnaReparto[]; onMonto: (clave: string, productoId: string, valor: string) => void }) {
  if (columnas.length === 0 || articulos.length === 0) return null;
  const porUnidad = (a: ArticuloReparto) => {
    const cant = leer(a.cantidad);
    return Money.sumar(columnas.map((c) => { const m = leer(c.montos[a.producto_id] ?? ''); return m && cant?.esPositivo() ? m.div(cant) : Money.cero(); }));
  };
  return (
    <div className="overflow-x-auto rounded-2xl border bg-card" data-testid="cp-matriz-reparto">
      <p className="border-b px-3 py-2 text-sm font-medium">Reparto de gastos al costo <span className="text-xs font-normal text-muted-foreground">· escribe cuánto va a cada artículo; cada gasto tiene que cuadrar al centavo</span><AyudaVentana tema="compras-editor.reparto" className="ml-1" /></p>
      <table className="w-full text-[13px]">
        <thead className="bg-muted/80 text-[11px] uppercase text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 text-left">Artículo</th>
            <th className="px-2 text-right">Unidades</th>
            <th className="px-2 text-right">Costo factura</th>
            {columnas.map((c) => <th key={c.clave} className="px-2 text-right">{c.titulo}<span className="block font-normal normal-case">{c.conFactura ? 'con factura' : 'sin factura'}</span></th>)}
            <th className="px-2 text-right">Gasto / unidad</th>
            <th className="px-2 text-right">Costo real</th>
          </tr>
        </thead>
        <tbody>
          {articulos.map((a) => {
            const gu = porUnidad(a);
            const factura = Money.parse(a.costo_factura);
            return (
              <tr key={a.producto_id} className="border-t" data-testid={`cp-reparto-fila-${a.codigo}`}>
                <td className="px-2 py-1">{a.codigo} <span className="text-muted-foreground">{a.nombre}</span></td>
                <td className="px-2 text-right num">{a.cantidad}</td>
                <td className="px-2 text-right num">{usd(factura)}</td>
                {columnas.map((c) => (
                  <td key={c.clave} className="px-2 py-1 text-right"><Input inputMode="decimal" value={c.montos[a.producto_id] ?? ''} onChange={(e) => onMonto(c.clave, a.producto_id, e.target.value)} className="h-8 w-24 text-right" aria-label={`${c.titulo} para ${a.codigo}`} data-testid={`cp-reparto-${c.clave}-${a.codigo}`} /></td>
                ))}
                <td className="px-2 text-right num" data-testid={`cp-reparto-gu-${a.codigo}`}>{con4(gu)}</td>
                <td className="px-2 text-right num font-medium" data-testid={`cp-reparto-real-${a.codigo}`}>{con4(factura.add(gu))}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t bg-muted/40 align-top">
          <tr>
            <td className="px-2 py-1.5 text-xs text-muted-foreground" colSpan={3}>Cuadre de cada gasto</td>
            {columnas.map((c) => <td key={c.clave} className="px-2 py-1.5"><PieCuadre c={cuadre(c.total, articulos.map((a) => c.montos[a.producto_id] ?? ''))} testid={`cp-cuadre-${c.clave}`} /></td>)}
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export interface FilaLibre { producto_id: string; codigo: string; nombre: string; unidades: string; monto: string; costo_real: string }

/**
 * Productos sueltos o los de una compra anterior. Las unidades vienen de la existencia actual o de la compra y se
 * pueden cambiar (nunca cero). `veCosto`: el costo real resultante solo para quien ve costos.
 */
export function RepartoLibre({ total, filas, onChange, compraId, onCompra, prefijo, veCosto }: { total: string; filas: FilaLibre[]; onChange: (f: FilaLibre[]) => void; compraId?: string | null; onCompra?: (id: string | null) => void; prefijo: string; veCosto: boolean }) {
  const [q, setQ] = useState('');
  const [qCompra, setQCompra] = useState('');
  const [modo, setModo] = useState<'productos' | 'compra'>(compraId ? 'compra' : 'productos');
  const productos = useQuery({ queryKey: ['costos', 'productos', q], queryFn: () => get<ProductoCostoDTO[]>('/costos/productos', { q, limite: 8 }), enabled: modo === 'productos' && q.trim().length > 0 });
  const compras = useQuery({ queryKey: ['costos', 'compras', qCompra], queryFn: () => get<CompraParaRepartirDTO[]>('/costos/compras', { q: qCompra || undefined, limite: 8 }), enabled: modo === 'compra' });
  const c = cuadre(total, filas.map((f) => f.monto));
  const set = (i: number, cambio: Partial<FilaLibre>) => onChange(filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)));
  const agregar = (p: ProductoCostoDTO) => {
    if (!filas.some((f) => f.producto_id === p.id)) onChange([...filas, { producto_id: p.id, codigo: p.codigo, nombre: p.nombre, unidades: Money.parse(p.existencia).esPositivo() ? p.existencia : '', monto: '', costo_real: p.costo_real }]);
    setQ('');
  };
  const elegirCompra = (x: CompraParaRepartirDTO) => {
    onCompra?.(x.id);
    onChange(x.productos.map((p) => ({ producto_id: p.producto_id, codigo: p.codigo, nombre: p.nombre, unidades: p.cantidad, monto: '', costo_real: '' })));
  };
  return (
    <div className="space-y-2 rounded-xl border p-3" data-testid={`${prefijo}-reparto`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">Repartir entre:</span>
        <Button size="sm" variant={modo === 'productos' ? 'default' : 'outline'} onClick={() => { setModo('productos'); onCompra?.(null); }} data-testid={`${prefijo}-modo-productos`}>Productos</Button>
        <Button size="sm" variant={modo === 'compra' ? 'default' : 'outline'} onClick={() => setModo('compra')} data-testid={`${prefijo}-modo-compra`}>Lo que trajo una compra</Button>
      </div>
      {modo === 'productos' ? (
        <div className="relative">
          <Input placeholder="Buscar producto por nombre o código…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8" data-testid={`${prefijo}-buscar-producto`} />
          {q.trim() && productos.data ? (
            <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-md">
              {productos.data.map((p) => <li key={p.id}><button className="flex w-full justify-between px-2 py-1 text-left text-sm hover:bg-accent" onClick={() => agregar(p)} data-testid={`${prefijo}-producto-${p.codigo}`}><span>{p.codigo} · {p.nombre}</span><span className="text-xs text-muted-foreground">existencia {Money.parse(p.existencia).toDisplay()}</span></button></li>)}
              {productos.data.length === 0 ? <li className="px-2 py-1.5 text-xs text-muted-foreground">Sin coincidencias.</li> : null}
            </ul>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1">
          <Input placeholder="Buscar compra por número, factura o proveedor…" value={qCompra} onChange={(e) => setQCompra(e.target.value)} className="h-8" data-testid={`${prefijo}-buscar-compra`} />
          <ul className="max-h-40 divide-y overflow-auto rounded-lg border text-sm">
            {(compras.data ?? []).map((x) => <li key={x.id}><button className={`flex w-full justify-between px-2 py-1 text-left hover:bg-accent ${x.id === compraId ? 'bg-accent' : ''}`} onClick={() => elegirCompra(x)} data-testid={`${prefijo}-compra-${x.numero_formateado ?? x.numero_factura}`}><span>{x.numero_formateado} · factura {x.numero_factura} · {x.proveedor_razon_social}</span><span className="text-xs text-muted-foreground">{x.fecha_factura} · {x.productos.length} artículo(s)</span></button></li>)}
            {compras.data && compras.data.length === 0 ? <li className="px-2 py-1.5 text-xs text-muted-foreground">Sin compras confirmadas con artículos.</li> : null}
          </ul>
        </div>
      )}
      {filas.length === 0 ? <p className="text-xs text-muted-foreground">Agrega los productos a los que va este gasto.</p> : (
        <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-[11px] uppercase text-muted-foreground"><tr><th className="py-1 text-left">Producto</th><th className="text-right">Unidades</th><th className="text-right">Monto $</th><th className="text-right">Gasto / unidad</th>{veCosto ? <th className="text-right">Costo real</th> : null}<th /></tr></thead>
          <tbody>
            {filas.map((f, i) => {
              const m = leer(f.monto);
              const u = leer(f.unidades);
              const gu = m && u?.esPositivo() ? m.div(u) : null;
              return (
                <tr key={f.producto_id} className="border-t" data-testid={`${prefijo}-fila-${f.codigo}`}>
                  <td className="py-1">{f.codigo} <span className="text-muted-foreground">{f.nombre}</span></td>
                  <td className="text-right"><Input inputMode="decimal" value={f.unidades} onChange={(e) => set(i, { unidades: e.target.value })} className={`h-8 w-20 text-right ${u?.esPositivo() ? '' : 'border-destructive'}`} aria-label={`Unidades de ${f.codigo}`} data-testid={`${prefijo}-unidades-${f.codigo}`} /></td>
                  <td className="text-right"><Input inputMode="decimal" value={f.monto} onChange={(e) => set(i, { monto: e.target.value })} className="h-8 w-24 text-right" aria-label={`Monto para ${f.codigo}`} data-testid={`${prefijo}-monto-${f.codigo}`} /></td>
                  <td className="text-right num" data-testid={`${prefijo}-gu-${f.codigo}`}>{gu ? con4(gu) : '—'}</td>
                  {veCosto ? <td className="text-right num">{f.costo_real && gu ? con4(Money.parse(f.costo_real).add(gu)) : '—'}</td> : null}
                  <td className="text-right"><Button size="icon" variant="ghost" onClick={() => onChange(filas.filter((_, j) => j !== i))} aria-label={`Quitar ${f.codigo}`}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      <PieCuadre c={c} testid={`${prefijo}-cuadre`} />
    </div>
  );
}

/** El reparto libre listo para enviarse: cuadra y todas las unidades son positivas. */
export const repartoLibreListo = (total: string, filas: FilaLibre[]) => filas.length > 0 && cuadre(total, filas.map((f) => f.monto)).cuadra && filas.every((f) => leer(f.monto)?.esPositivo() && leer(f.unidades)?.esPositivo());
/** Filas con monto (las vacías no van) en el formato de la API. */
export const repartoLibreAEnviar = (filas: FilaLibre[]) => filas.filter((f) => leer(f.monto)?.esPositivo()).map((f) => ({ producto_id: f.producto_id, monto: leer(f.monto)!.toDocumento(), unidades: leer(f.unidades)!.toString() }));
